// =====================================================================
// src/screens/v2/SubscriptionBanner.jsx
//
// Merchant subscription / trial status banner. Renders at the top of
// the Today tab.
//
// Session 6c2a — rewrote to:
//   1. Remove WhatsApp deep-link "Activate now" flow. The CTA now
//      invokes the onActivate callback passed by MerchantApp, which
//      opens the Aangan Support chat modal with a pre-filled
//      reactivation message. Admin sees the request in Support Inbox
//      (Session 5a) and can activate via the ActivationModal (Session
//      6a) — full round-trip stays inside the app.
//   2. Use the shared isBusinessActive / getSubscriptionState helpers
//      (Session 6c1) so the banner's expired branch fires for BOTH
//      trial-expired AND paid-activation-expired states. Previously
//      only trial-expired was handled.
//
// States rendered:
//   'active-trial'  — Countdown to trial end (existing behavior kept).
//   'expired'       — Warning banner. Copy differs subtly by whether
//                     the last active period was a trial (still shows
//                     "15-day trial ended") vs. a paid activation
//                     ("subscription ended").
//   'active-paid'   — Not rendered. Owner has an active paid subscription;
//                     no banner needed.
//   'never'         — Not rendered. Legacy / seed records without any
//                     trial or activation state.
//
// Props:
//   business    — biz doc with trialStartedAt, trialDays, activated,
//                 activationValidTill, activationDurationMonths.
//   onActivate  — callback invoked when the Activate/Reactivate CTA is
//                 tapped. Parent (MerchantApp) opens the support chat
//                 modal with a pre-filled message.
// =====================================================================

import React from 'react';
import { Clock, AlertCircle } from 'lucide-react';
import { isBusinessActive, getSubscriptionState } from '../../lib/v2/isBusinessActive';

function daysBetween(startMs, endMs) {
  const diffMs = endMs - startMs;
  return Math.floor(diffMs / (24 * 60 * 60 * 1000));
}

function formatDate(ms) {
  const d = new Date(ms);
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

// Normalize a Firestore Timestamp / Date / number → epoch ms, else null.
function toMillis(v) {
  if (v == null) return null;
  if (typeof v === 'number') return v;
  if (typeof v?.toMillis === 'function') return v.toMillis();
  if (typeof v?.seconds === 'number') return v.seconds * 1000;
  if (v instanceof Date) return v.getTime();
  return null;
}

export default function SubscriptionBanner({ business, onActivate }) {
  if (!business) return null;

  const trialStartMs = toMillis(business.trialStartedAt);
  const trialDays = Number.isFinite(business.trialDays) ? business.trialDays : 15;
  const trialEndMs = trialStartMs != null ? trialStartMs + trialDays * 24 * 60 * 60 * 1000 : null;
  const validTillMs = toMillis(business.activationValidTill);

  const state = getSubscriptionState(business);
  const bizActive = isBusinessActive(business);

  // Don't render for the two "nothing to worry about" states. Legacy
  // records without any trial/activation history (never) also get no
  // banner — matches original behavior.
  if (state === 'active-paid' || state === 'never') return null;

  // ---- Render: trial active (countdown) ----
  if (state === 'active-trial' && trialEndMs != null) {
    const daysLeft = daysBetween(Date.now(), trialEndMs);
    // Highlight urgency in the last 3 days.
    const urgent = daysLeft <= 3;
    return (
      <div className="v2m-sub-banner" style={banner.trial}>
        <div style={banner.iconWrap(urgent ? '#C45A3F' : '#B8893A')}>
          <Clock size={18} color="#fff" />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={banner.title}>
            {urgent
              ? `Trial ends in ${daysLeft} day${daysLeft === 1 ? '' : 's'}`
              : `Free trial · ${daysLeft} day${daysLeft === 1 ? '' : 's'} left`}
          </div>
          <div style={banner.text}>
            Your Aangan Connect trial is valid till <b>{formatDate(trialEndMs)}</b>.
            {urgent
              ? ' Activate now to keep your listing active.'
              : ' Customers can call, WhatsApp, and order from your listing during this period.'}
          </div>
          <button onClick={onActivate} style={banner.actionBtn} type="button">
            Activate now
          </button>
        </div>
      </div>
    );
  }

  // ---- Render: expired ----
  // Two flavors of expired copy: trial-expired keeps the original
  // "15-day trial ended on X" line the owner already knows; paid-expired
  // is the new post-6c2a case (activation window has lapsed).
  if (state === 'expired' && !bizActive) {
    // Distinguish which timeline was most recently the active one.
    // If the biz was ever paid-activated (validTillMs present), that's
    // the more recent expiry; otherwise it's the trial that expired.
    const wasPaid = validTillMs != null;
    const expiryMs = wasPaid ? validTillMs : trialEndMs;

    return (
      <div className="v2m-sub-banner" style={banner.lapsed}>
        <div style={banner.iconWrap('#C45A3F')}>
          <AlertCircle size={18} color="#fff" />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={banner.title}>
            {wasPaid
              ? 'Aangan Connect ended · Listing inactive'
              : 'Trial expired · Listing inactive'}
          </div>
          <div style={banner.text}>
            {wasPaid ? (
              <>
                Your Aangan Connect ended on <b>{formatDate(expiryMs)}</b>.
                Reactivate to keep accepting orders, calls, and WhatsApp from customers.
              </>
            ) : (
              <>
                Your {trialDays}-day trial ended on <b>{formatDate(expiryMs)}</b>.
                Activate Aangan Connect to keep accepting orders, calls,
                and WhatsApp from customers.
              </>
            )}
          </div>
          <button onClick={onActivate} style={banner.actionBtnCritical} type="button">
            {wasPaid ? 'Reactivate now' : 'Activate now'}
          </button>
        </div>
      </div>
    );
  }

  // Fallback safety — shouldn't hit here for the states above.
  return null;
}

// ---- Styles (inline rather than a CSS file — small, scoped, easy to read) ----
const banner = {
  trial: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    padding: 14,
    margin: '12px 16px',
    background: 'var(--accent-soft)',
    border: '1px solid var(--border)',
    borderRadius: 14,
  },
  lapsed: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    padding: 14,
    margin: '12px 16px',
    background: '#FDF1ED',
    border: '1px solid #E8C8BB',
    borderRadius: 14,
  },
  iconWrap: (bg) => ({
    width: 32,
    height: 32,
    borderRadius: 8,
    background: bg,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  }),
  title: {
    fontSize: 14,
    fontWeight: 600,
    color: 'var(--ink)',
    marginBottom: 4,
    lineHeight: 1.3,
  },
  text: {
    fontSize: 12,
    color: 'var(--ink-soft)',
    lineHeight: 1.5,
  },
  actionBtn: {
    marginTop: 10,
    padding: '7px 14px',
    background: 'var(--ink)',
    color: '#fff',
    border: 'none',
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  actionBtnCritical: {
    marginTop: 10,
    padding: '7px 14px',
    background: '#C45A3F',
    color: '#fff',
    border: 'none',
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
};
